import React from 'react';
import type { Source, SourceHealth } from '@/types';
import ActivityLogPanel from './activity/ActivityLogPanel';
import {
  formatSuccessRate,
  formatTimestamp,
  sourceHealthDetailHint,
  sourceHealthLabel,
  sourceHealthPreviousLabel,
  sourceHealthRecommendedAction,
  sourceHealthTitle,
  sourceHealthVariant,
} from '@/utils/sourceHealth';
import { StatusBadge, SourceChips } from './ui';

type Props = {
  source: Source;
  health: SourceHealth | undefined;
  onClose: () => void;
};

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="diagnostics-row">
      <dt>{label}</dt>
      <dd>{value ?? '—'}</dd>
    </div>
  );
}

export default function SourceDiagnosticsModal({ source, health, onClose }: Props) {
  if (!health) {
    return (
      <div className="modal-backdrop" role="presentation" onClick={onClose}>
        <div className="modal card" role="dialog" aria-labelledby="diag-title" onClick={(e) => e.stopPropagation()}>
          <h2 id="diag-title">
            Diagnostics — <SourceChips sources={[source.name]} enabledBySource={{ [source.name]: source.is_enabled }} />
          </h2>
          <p className="muted">No health data available.</p>
          <button type="button" className="btn" onClick={onClose}>Close</button>
        </div>
      </div>
    );
  }

  const recommended = sourceHealthRecommendedAction(health);

  return (
    <div className="modal-backdrop" role="presentation" onClick={onClose}>
      <div className="modal card source-diagnostics-modal" role="dialog" aria-labelledby="diag-title" onClick={(e) => e.stopPropagation()}>
        <h2 id="diag-title">
          Diagnostics — <SourceChips sources={[source.name]} enabledBySource={{ [source.name]: source.is_enabled }} />
        </h2>
        <div className="source-diagnostics-summary">
          <StatusBadge variant={sourceHealthVariant(health)} title={sourceHealthTitle(health)}>
            {sourceHealthLabel(health)}
          </StatusBadge>
        </div>
        <dl className="source-diagnostics-grid">
          <Row label="Current status" value={health.status} />
          <Row label="Current error" value={health.current_error_code ?? 'OK'} />
          <Row label="Current message" value={health.current_error_message} />
          <Row label="Info" value={health.info_code ? `${health.info_code}: ${health.info_message ?? ''}` : null} />
          <Row label="Previous error" value={sourceHealthPreviousLabel(health)} />
          <Row label="Root cause" value={health.root_error_code} />
          <Row label="Root message" value={health.root_error_message} />
          <Row label="Cooldown until" value={formatTimestamp(health.cooldown_until)} />
          <Row label="Last success" value={formatTimestamp(health.last_success_at)} />
          <Row label="Last failure" value={formatTimestamp(health.last_failure_at)} />
          <Row label="Links (24h)" value={health.links_24h} />
          <Row label="Failures (24h)" value={health.failures_24h} />
          <Row label="Success rate (24h)" value={formatSuccessRate(health.success_rate_24h)} />
          <Row label="Zero-result runs (24h)" value={health.zero_result_runs_24h} />
          <Row label="Consecutive zero-result runs" value={health.consecutive_zero_result_runs} />
          <Row label="Final URL" value={health.final_url ? <code className="break-all">{health.final_url}</code> : null} />
          <Row label="Recommended action" value={recommended} />
        </dl>
        {sourceHealthDetailHint(health) && (
          <p className="muted">{sourceHealthDetailHint(health)}</p>
        )}
        <ActivityLogPanel
          title="Recent activity"
          sourceId={source.id}
          limit={8}
          compact
          showClear
        />
        <div className="form-actions">
          <button type="button" className="btn btn-primary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
